import { ComboBox, Dot, getSymbol } from "@xola/ui-kit";
import { FormField, FormFieldContext, QuestionnaireDescription, useError } from "common";
import dayjs from "dayjs";
import { useField } from "formik";
import { keyBy } from "lodash";
import React, { useContext, useMemo, useState } from "react";
import { AmountType, PostalAddress, PurchaseItem, QuestionnairePricingStructure } from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { PickupFieldProps } from "./TextField";
import { useSeller } from "@/hooks/useAccount";
import { CreatePurchaseItemState } from "@/stores/createPurchaseStore";

export interface PickupLocationSelectFieldProps extends PickupFieldProps {
    item: CreatePurchaseItemState | PurchaseItem;
    showDescription?: boolean;
    onChange?: (value: string) => void;
}

interface Option {
    id: string;
    name: string;
    price: string;
    fullAddress: string;
    arrivalFormatted: string;
    priceTypeText: string;
    declinePickup: boolean;
}

interface OptionWithSequence {
    sequence: number;
    pickupLocation: { id: string };
    localOverrides?: any;
}

export const PickupLocationSelectField = ({
    name,
    field,
    classNames,
    item,
    showDescription = true,
    onChange,
}: PickupLocationSelectFieldProps) => {
    const seller = useSeller();

    const [inputText, setInputText] = useState("");
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const fieldName = (useContext(FormFieldContext) || name) ?? "";
    const [formikField, , helpers] = useField(fieldName);

    const error = useError(fieldName);

    const currency = seller?.currency;

    const filteredList = useMemo(() => {
        const validOptions = (field.options as OptionWithSequence[]).filter((opt) => opt?.pickupLocation?.id);

        const optionsMap: Record<string, OptionWithSequence> = keyBy(validOptions, (opt) => opt.pickupLocation.id);

        return field.options
            .flatMap((opt) => opt.pickupLocation)
            .sort((a, b) => {
                const seqA = optionsMap[a.id]?.sequence ?? 0;
                const seqB = optionsMap[b.id]?.sequence ?? 0;
                return seqA - seqB;
            });
    }, [field]);

    const selected = useMemo(
        () => filteredList.find((opt) => opt.id === formikField.value) ?? null,
        [formikField.value, filteredList],
    );

    const optionsForCombobox = filteredList.map((option) => {
        const fullAddress = getAddressFromLocation(option?.location);

        return {
            id: option.id,
            name: option.name,
            fullAddress,
            declinePickup: option.parent ? false : option.isDeclinePickupItem,
            price: formatPickupPrice(
                option,
                currency,
                field.priceType === QuestionnairePricingStructure.PER_PERSON ? false : true,
            ),
            priceTypeText: field.priceType === QuestionnairePricingStructure.PER_PERSON ? "/ person" : "/ group",
            arrivalFormatted:
                "arrivalTime" in item && item.arrivalTime && option.arrivalOffset
                    ? getFormattedPickupLocationTime(item.arrivalDate, item.arrivalTime, option.arrivalOffset)
                    : null,
        };
    });

    const handleSelect = (option: Option) => {
        helpers.setValue(option.id);
        onChange?.(option.id);

        setInputText(option.name);
        setMenuIsOpen(false);
    };

    return (
        <FormField label={field.label} name={fieldName} classNames={{ label: classNames?.label }}>
            {showDescription && <QuestionnaireDescription field={field} />}

            <div className="relative">
                <ComboBox
                    blurInputOnSelect
                    menuIsOpen={menuIsOpen}
                    options={optionsForCombobox}
                    getOptionValue={(option: Option) => option.id}
                    getOptionLabel={(option: Option) => option.name}
                    isError={error}
                    isRequired={field.required}
                    value={selected ?? null}
                    inputValue={inputText}
                    placeholder={
                        <NoTranslate as="div" className="text-gray-darker">
                            Select an option
                        </NoTranslate>
                    }
                    components={{
                        Option: (props: { data: Option }) => (
                            <div
                                className="flex cursor-pointer items-center justify-between gap-2 px-2 py-1 hover:bg-gray-lighter"
                                onClick={() => handleSelect(props.data)}
                            >
                                <span className="flex flex-col text-gray-darker">
                                    <span className="flex items-center space-x-1 text-gray-darker">
                                        <p className="font-bold">{props.data.name}</p>
                                        {props.data.arrivalFormatted && (
                                            <p className="text-sm">{` (${props.data.arrivalFormatted})`}</p>
                                        )}
                                    </span>
                                    {props.data.fullAddress && <span>{props.data.fullAddress}</span>}
                                </span>
                                <span className="text-gray-darker">
                                    {`${props.data.price} ${props.data.declinePickup ? "" : props.data.priceTypeText}`}
                                </span>
                            </div>
                        ),
                    }}
                    filterOption={() => true}
                    onMenuOpen={() => setMenuIsOpen(true)}
                    onMenuClose={() => setMenuIsOpen(false)}
                    onFocus={(e: any) => e.preventDefault()}
                    onClick={(e: any) => e.preventDefault()}
                    onInputChange={(text: string) => setInputText(text)}
                    onChange={handleSelect}
                />

                {field.required && !selected?.name && (
                    <span className="absolute right-7 top-[15px] -translate-y-1/2 select-none text-lg">
                        <Dot color="danger" />
                    </span>
                )}
            </div>
        </FormField>
    );
};

export const formatPickupPrice = (row: any, currency: string, isOrganizer: boolean) => {
    const priceInfo = isOrganizer ? row.prices?.perGroup : row.prices?.perPerson;
    if (!priceInfo || priceInfo.price == null) return "$0";

    const prefix = priceInfo.priceType === AmountType.ABSOLUTE ? getSymbol(currency) : "";
    const suffix = priceInfo.priceType === AmountType.PERCENT ? " %" : "";

    return `${prefix}${priceInfo.price}${suffix}`;
};

export const getFormattedPickupLocationTime = (arrivalDate?: string, arrivalTime?: number, arrivalOffset?: number) => {
    if (!arrivalDate || !arrivalTime) return null;

    const date = dayjs(arrivalDate);
    const timeString = arrivalTime.toString().padStart(4, "0");
    const hours = Number(timeString.slice(0, 2));
    const minutes = Number(timeString.slice(2));

    let pickupTime = date.hour(hours).minute(minutes).second(0);

    if (arrivalOffset != null) {
        pickupTime = pickupTime.subtract(arrivalOffset, "minute");
    }

    const eventDate = date;

    if (pickupTime.isSame(eventDate, "day")) {
        return pickupTime.format("h:mm A");
    } else if (pickupTime.isSame(eventDate, "year")) {
        return pickupTime.format("MMM D, h:mm A");
    } else {
        return pickupTime.format("MMM D, YYYY, h:mm A");
    }
};

const getAddressFromLocation = (location?: {
    postalAddress?: PostalAddress;
    name?: string;
    formattedAddress?: string;
}): string => {
    const address = location?.postalAddress;
    const name = location?.name;
    const formattedAddress = location?.formattedAddress;

    if (name && formattedAddress) {
        if (formattedAddress.toLowerCase().startsWith(name.toLowerCase())) {
            return formattedAddress;
        }
        return `${name}, ${formattedAddress}`;
    }

    if (address) {
        return [address.streetLines, address.region, address.countryCode]
            .filter((location) => typeof location === "string" && location.trim() !== "")
            .join(", ");
    }

    return "";
};
